import {
  PbPatchReturnProcessConditionsReq,
  PbReturnProcessConditions
} from '@itsrever/rever-api'

export type NonReturnableLists = {
  productIds: string[]
  collectionIds: string[]
  productTypes: string[]
  productTags: string[]
  orderTags: string[]
}

export type NonReturnableListKey = keyof NonReturnableLists

// Product types live in non_returnable_category_ids: on Shopify a product's category is its type.
const requestFields = {
  productIds: 'non_returnable_product_ids',
  collectionIds: 'non_returnable_collection_ids',
  productTypes: 'non_returnable_category_ids',
  productTags: 'non_returnable_tag_ids',
  orderTags: 'non_returnable_order_tag_ids'
} as const satisfies Record<
  NonReturnableListKey,
  keyof PbReturnProcessConditions & keyof PbPatchReturnProcessConditionsReq
>

const listKeys = Object.keys(requestFields) as NonReturnableListKey[]

// The lists each platform's evaluator compares; a list missing here would be saved and never used.
const listsByPlatform: Record<string, NonReturnableListKey[]> = {
  SHOPIFY: listKeys,
  WOOCOMMERCE: ['productIds', 'productTypes', 'productTags'],
  PRESTASHOP: ['productIds', 'productTypes', 'productTags'],
  SHOPWARE: ['productIds', 'productTypes', 'productTags'],
  SALESFORCE: ['productIds', 'productTypes', 'collectionIds'],
  CUSTOM_INTEGRATION_V2: [
    'productIds',
    'productTypes',
    'productTags',
    'collectionIds'
  ],
  MAGENTO_V2: ['productIds', 'productTypes'],
  COMMERCELAYER: ['productIds']
}

export function nonReturnableListsFor(
  platform: string | undefined
): NonReturnableListKey[] {
  return (platform && listsByPlatform[platform]) || []
}

export const emptyNonReturnableLists: NonReturnableLists = {
  productIds: [],
  collectionIds: [],
  productTypes: [],
  productTags: [],
  orderTags: []
}

export function toNonReturnableLists(
  conditions: PbReturnProcessConditions
): NonReturnableLists {
  const lists = { ...emptyNonReturnableLists }
  listKeys.forEach((key) => {
    lists[key] = conditions[requestFields[key]] ?? []
  })
  return lists
}

export function changedNonReturnableLists(
  original: NonReturnableLists,
  edited: NonReturnableLists
): NonReturnableListKey[] {
  return listKeys.filter(
    (key) =>
      original[key].length !== edited[key].length ||
      original[key].some((id) => !edited[key].includes(id))
  )
}

// Sends only the edited lists, each whole: the endpoint replaces a list it receives.
export function toNonReturnablePatchRequest(
  ecommerceId: string,
  original: NonReturnableLists,
  edited: NonReturnableLists
): PbPatchReturnProcessConditionsReq {
  const request: PbPatchReturnProcessConditionsReq = {
    ecommerce_id: ecommerceId
  }
  changedNonReturnableLists(original, edited).forEach((key) => {
    request[requestFields[key]] = { values: edited[key] }
  })
  return request
}
